/**
 * 🚀 简化的 SuggestionManager
 * 只依赖：
 * - ✅ SuggestionSandboxManager
 * - ✅ SuggestionUIManager
 * - ✅ ySuggestionInfo (observe)
 */

import { Subject } from 'rxjs';
import type { DocService } from '../../doc';
import type { EditorService } from '../../editor';
import type { Suggestion } from '../types/suggestion.d';
import { SuggestionUIManager } from '../ui/suggestion-ui-manager';
import { SuggestionSandboxManager } from './suggestion-manager-sandbox';

export class SuggestionManager {
  // UI 管理器
  private readonly _uiManager: SuggestionUIManager;

  // ✅ 沙箱管理器（替代 Applicator + Factory）
  private _sandboxManager: SuggestionSandboxManager | null = null;

  private readonly _sugManagerId: string;
  private _ySuggestionInfo: any | null = null;
  private _yObserver: ((event: any) => void) | null = null;
  private _initializationTimer: number | null = null;
  private _disposed = false;

  // ✅ Accept All / Reject All 事件监听器引用
  private _allOperationEventHandler: EventListener | null = null;

  // Observables（用于外部监听）
  readonly onSuggestionAdded$ = new Subject<{ suggestion: Suggestion }>();
  readonly onSuggestionUpdated$ = new Subject<{
    suggestion: Suggestion;
    state: string;
  }>();
  readonly onSuggestionRemoved$ = new Subject<{ suggestionId: string }>();

  constructor(
    private readonly _docService: DocService,
    private readonly _editorService: EditorService
  ) {
    this._sugManagerId = `SM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this._uiManager = new SuggestionUIManager();
    this._setupAllOperationEventListener(); // ✅ 新增

    // 延迟初始化
    this._initializationTimer = setTimeout(() => {
      this._initializationTimer = null;
      if (!this._disposed) {
        this._initializeWithObserve();
      }
    }, 100) as unknown as number;

    console.log(`✅ [${this._sugManagerId}] SuggestionManager 初始化完成`);
  }

  dispose(): void {
    console.log(`🧹 [${this._sugManagerId}] Disposing SuggestionManager`);

    this._disposed = true;

    // 清理定时器
    if (this._initializationTimer !== null) {
      clearTimeout(this._initializationTimer);
      this._initializationTimer = null;
    }

    // ✅ 取消当前批次并清理沙箱管理器
    if (this._sandboxManager) {
      this._sandboxManager.cancelCurrentBatch();
      this._sandboxManager = null;
    }

    // ✅ 移除 observe
    if (this._yObserver && this._ySuggestionInfo) {
      this._ySuggestionInfo.unobserve(this._yObserver);
      this._yObserver = null;
    }
    this._ySuggestionInfo = null;

    // ✅ 移除 Accept All / Reject All 事件监听器
    if (this._allOperationEventHandler) {
      document.removeEventListener(
        'suggestion-all-action',
        this._allOperationEventHandler
      );
      this._allOperationEventHandler = null;
      console.log(`🧹 [${this._sugManagerId}] Accept/Reject All 事件监听器已移除`);
    }

    // ✅ 清理 UI Manager（会清理所有 timeout 和 MutationObserver）
    this._uiManager.dispose();

    // 完成 observables
    this.onSuggestionAdded$.complete();
    this.onSuggestionUpdated$.complete();
    this.onSuggestionRemoved$.complete();
  }

  /**
   * ✅ 设置 Accept All / Reject All 事件监听器
   */
  private _setupAllOperationEventListener(): void {
    this._allOperationEventHandler = ((e: CustomEvent) => {
      const { action } = e.detail;

      const docId = this._docService.doc?.meta$.getValue().id || '';

      if (action === 'accept-all') {
        console.log('🔄 Accept All 操作触发');
        this.applyAllSuggestions(docId).catch(console.error);
      } else if (action === 'reject-all') {
        console.log('🔄 Reject All 操作触发');
        this.rejectAllSuggestions(docId).catch(console.error);
      }
    }) as EventListener;

    document.addEventListener(
      'suggestion-all-action',
      this._allOperationEventHandler
    );

    console.log(`✅ [${this._sugManagerId}] Accept/Reject All 事件监听器已注册`);
  }

  /**
   * 🔑 核心方法：批量创建 suggestions（使用沙箱）
   */
  async batchCreateSuggestions(
    suggestions: Suggestion[]
  ): Promise<Map<string, any>> {
    const store = this._getStore();
    const host = this._editorService.editor.editorContainer$.value?.host;

    if (!store || !host) {
      console.warn(`⚠️ [${this._sugManagerId}] Store 或 Host 未就绪`);
      return new Map();
    }

    const incomingBatchId = suggestions[0]?.batch_id;
    if (!incomingBatchId) {
      console.warn(`⚠️ [${this._sugManagerId}] Suggestions 缺少 batch_id`);
      return new Map();
    }

    try {
      // ============================================
      // 初始化或获取沙箱管理器
      // ============================================
      if (!this._sandboxManager) {
        console.log(`🏗️ [${this._sugManagerId}] 初始化沙箱管理器`);
        this._sandboxManager = new SuggestionSandboxManager(store, host);
      }

      // ============================================
      // 检查当前批次状态
      // ============================================
      const currentStatus = this._sandboxManager.getCurrentBatchStatus();

      if (currentStatus.isProcessing) {
        const incomingCreatedAt = Math.max(
          ...suggestions.map(s => s.createdAt)
        );

        console.log(
          `🔍 [${this._sugManagerId}] 当前批次: batch_id=${currentStatus.batchId}, createdAt=${currentStatus.createdAt}`
        );
        console.log(
          `🔍 [${this._sugManagerId}] 新批次: batch_id=${incomingBatchId}, createdAt=${incomingCreatedAt}`
        );

        if (currentStatus.batchId !== incomingBatchId) {
          console.log(
            `🔄 [${this._sugManagerId}] 新批次 ${incomingBatchId} 到达，将自动中断旧批次 ${currentStatus.batchId}`
          );
        } else if (incomingCreatedAt > (currentStatus.createdAt || 0)) {
          console.log(
            `🔄 [${this._sugManagerId}] 检测到流式更新，将中断旧批次并处理新数据`
          );
        } else {
          console.log(
            `⏭️ [${this._sugManagerId}] 批次 ${incomingBatchId} 已在处理中`
          );
        }
      }

      // ============================================
      // 使用沙箱方案处理（带中断机制）
      // ============================================
      console.log(
        `🚀 [${this._sugManagerId}] 开始沙箱处理批次: ${incomingBatchId}, ${suggestions.length} suggestions`
      );

      const result = await this._sandboxManager.applyBatchInSandbox(
        suggestions,
        incomingBatchId,
        {
          onProgress: (progress: number, message: string) => {
            console.log(
              `📊 [${this._sugManagerId}] [${incomingBatchId}] ${(progress * 100).toFixed(0)}%: ${message}`
            );
          },
        }
      );

      // ============================================
      // 处理结果
      // ============================================
      if (!result.success) {
        if (result.aborted) {
          console.log(
            `⚠️ [${this._sugManagerId}] 批次 ${incomingBatchId} 被中断`
          );
        } else {
          console.error(
            `❌ [${this._sugManagerId}] 批次 ${incomingBatchId} 处理失败`
          );
        }
        return new Map();
      }

      console.log(
        `✅ [${this._sugManagerId}] 批次 ${incomingBatchId} 创建完成: ${result.results.size} suggestions`
      );

      // ✅ observe 会自动触发 UI 更新

      // 触发 observables
      suggestions.forEach(s => {
        this.onSuggestionAdded$.next({ suggestion: s });
      });

      return result.results;
    } catch (error) {
      console.error(`❌ [${this._sugManagerId}] 批量创建异常:`, error);
      return new Map();
    }
  }

  /**
   * 🔧 取消当前批次
   */
  cancelCurrentBatch(): void {
    if (!this._sandboxManager) {
      console.warn(`⚠️ [${this._sugManagerId}] 沙箱管理器未初始化`);
      return;
    }

    console.log(`⚠️ [${this._sugManagerId}] 手动取消当前批次`);
    this._sandboxManager.cancelCurrentBatch();
  }

  /**
   * 🔍 获取当前批次状态
   */
  getCurrentBatchStatus(): {
    batchId: string | null;
    createdAt: number | null;
    isProcessing: boolean;
  } {
    if (!this._sandboxManager) {
      return {
        batchId: null,
        createdAt: null,
        isProcessing: false,
      };
    }

    return this._sandboxManager.getCurrentBatchStatus();
  }

  /**
   * 🔍 预览批次效果
   */
  // async previewBatchSuggestions(
  //   suggestions: Suggestion[]
  // ): Promise<any> {
  //   const store = this._getStore();
  //   const host = this._editorService.editor.editorContainer$.value?.host;

  //   if (!store || !host) {
  //     console.warn('Store 或 Host 未就绪');
  //     return null;
  //   }

  //   if (!this._sandboxManager) {
  //     this._sandboxManager = new SuggestionSandboxManager(store, host);
  //   }

  //   return await this._sandboxManager.previewInSandbox(suggestions);
  // }

  /**
   * 🔑 应用单个 suggestion
   */
  async applySuggestion(suggestionId: string): Promise<void> {
    const store = this._getStore();
    if (!store) {
      console.warn('Store 未就绪');
      return;
    }

    try {
      const suggestions = this._getSuggestionsFromY();
      const suggestion = suggestions.find(s => s.id === suggestionId);

      if (!suggestion) {
        console.warn(`Suggestion ${suggestionId} 不存在`);
        return;
      }

      // ✅ 应用逻辑：删除旧 blocks，保留新 blocks
      store.transact(() => {
        // 删除 target blocks, insert类型targetBlock只做锚点使用
        if (suggestion.type !== 'insert') {
          suggestion.targetBlockIds.forEach(blockId => {
            const block = store.getBlock(blockId);
            if (block) {
              store.deleteBlock(blockId);
            }
          });
        }

        // 清理所有 blocks 的 suggestionState
        const allBlockIds = [
          ...suggestion.targetBlockIds,
          ...(suggestion.newBlockIds || []),
        ];

        allBlockIds.forEach(blockId => {
          const block = store.getBlock(blockId);
          if (block) {
            block.yBlock.delete('suggestionState');
          }
        });

        // 更新 ySuggestionInfo 状态
        const updatedSuggestions = suggestions.map(s =>
          s.id === suggestionId ? { ...s, status: 'accepted' as const } : s
        );
        this._ySuggestionInfo?.set('list', { list: updatedSuggestions });
      });

      console.log(`✅ Suggestion ${suggestionId} 已应用`);

      // 触发 observable
      this.onSuggestionUpdated$.next({
        suggestion,
        state: 'accepted',
      });
    } catch (error) {
      console.error(`❌ 应用 suggestion ${suggestionId} 失败:`, error);
    }
  }

  /**
   * 🔑 拒绝单个 suggestion
   */
  async rejectSuggestion(suggestionId: string): Promise<void> {
    const store = this._getStore();
    if (!store) {
      console.warn('Store 未就绪');
      return;
    }

    try {
      const suggestions = this._getSuggestionsFromY();
      const suggestion = suggestions.find(s => s.id === suggestionId);

      if (!suggestion) {
        console.warn(`Suggestion ${suggestionId} 不存在`);
        return;
      }

      // ✅ 拒绝逻辑：删除新 blocks，保留旧 blocks
      store.transact(() => {
        // 删除新创建的 blocks
        if (suggestion.newBlockIds && suggestion.newBlockIds.length > 0) {
          suggestion.newBlockIds.forEach(blockId => {
            const block = store.getBlock(blockId);
            if (block) {
              store.deleteBlock(blockId);
            }
          });
        }

        // 清理所有 blocks 的 suggestionState
        const allBlockIds = [
          ...suggestion.targetBlockIds,
          ...(suggestion.newBlockIds || []),
        ];

        allBlockIds.forEach(blockId => {
          const block = store.getBlock(blockId);
          if (block) {
            block.yBlock.delete('suggestionState');
          }
        });

        // 更新 ySuggestionInfo 状态
        const updatedSuggestions = suggestions.map(s =>
          s.id === suggestionId ? { ...s, status: 'rejected' as const } : s
        );
        this._ySuggestionInfo?.set('list', { list: updatedSuggestions });
      });

      console.log(`✅ Suggestion ${suggestionId} 已拒绝`);

      // 触发 observable
      this.onSuggestionUpdated$.next({
        suggestion,
        state: 'rejected',
      });
    } catch (error) {
      console.error(`❌ 拒绝 suggestion ${suggestionId} 失败:`, error);
    }
  }

  /**
   * ✅ 批量应用所有 suggestions（单个 transaction）
   */
  async applyAllSuggestions(_docId: string): Promise<void> {
    const store = this._getStore();
    if (!store) {
      console.warn('Store 未就绪');
      return;
    }

    try {
      const suggestions = this._getSuggestionsFromY();
      const pendingSuggestions = suggestions.filter(
        s => s.status === 'pending'
      );

      if (pendingSuggestions.length === 0) {
        console.log(`ℹ️ [${this._sugManagerId}] 没有待处理的 suggestions`);
        return;
      }

      console.log(
        `🔄 [${this._sugManagerId}] 应用所有 ${pendingSuggestions.length} 个 suggestions（单个 transaction）`
      );

      // ✅ 在一个 transaction 中完成所有操作
      store.transact(() => {
        pendingSuggestions.forEach(suggestion => {
          // 1. 删除 target blocks, insert类型targetBlock只做锚点使用
          if (suggestion.type !== 'insert') {
            suggestion.targetBlockIds.forEach(blockId => {
              const block = store.getBlock(blockId);
              if (block) {
                store.deleteBlock(blockId);
              }
            });
          }

          // 2. 清理所有 blocks 的 suggestionState
          const allBlockIds = [
            ...suggestion.targetBlockIds,
            ...(suggestion.newBlockIds || []),
          ];

          allBlockIds.forEach(blockId => {
            const block = store.getBlock(blockId);
            if (block) {
              block.yBlock.delete('suggestionState');
            }
          });
        });

        // 3. 更新 ySuggestionInfo（一次性更新所有状态）
        const updatedSuggestions = suggestions.map(s =>
          s.status === 'pending' ? { ...s, status: 'accepted' as const } : s
        );
        this._ySuggestionInfo?.set('list', { list: updatedSuggestions });
      });

      console.log(
        `✅ [${this._sugManagerId}] 所有 suggestions 已应用（单个 transaction 完成）`
      );
    } catch (error) {
      console.error(`❌ [${this._sugManagerId}] Accept All 失败:`, error);
    }
  }

  /**
   * ✅ 批量拒绝所有 suggestions（单个 transaction）
   */
  async rejectAllSuggestions(_docId: string): Promise<void> {
    const store = this._getStore();
    if (!store) {
      console.warn('Store 未就绪');
      return;
    }

    try {
      const suggestions = this._getSuggestionsFromY();
      const pendingSuggestions = suggestions.filter(
        s => s.status === 'pending'
      );

      if (pendingSuggestions.length === 0) {
        console.log(`ℹ️ [${this._sugManagerId}] 没有待处理的 suggestions`);
        return;
      }

      console.log(
        `🔄 [${this._sugManagerId}] 拒绝所有 ${pendingSuggestions.length} 个 suggestions（单个 transaction）`
      );

      // ✅ 在一个 transaction 中完成所有操作
      store.transact(() => {
        pendingSuggestions.forEach(suggestion => {
          // 1. 删除 new blocks
          if (suggestion.newBlockIds && suggestion.newBlockIds.length > 0) {
            suggestion.newBlockIds.forEach(blockId => {
              const block = store.getBlock(blockId);
              if (block) {
                store.deleteBlock(blockId);
              }
            });
          }

          // 2. 清理所有 blocks 的 suggestionState
          const allBlockIds = [
            ...suggestion.targetBlockIds,
            ...(suggestion.newBlockIds || []),
          ];

          allBlockIds.forEach(blockId => {
            const block = store.getBlock(blockId);
            if (block) {
              block.yBlock.delete('suggestionState');
            }
          });
        });

        // 3. 更新 ySuggestionInfo（一次性更新所有状态）
        const updatedSuggestions = suggestions.map(s =>
          s.status === 'pending' ? { ...s, status: 'rejected' as const } : s
        );
        this._ySuggestionInfo?.set('list', { list: updatedSuggestions });
      });

      console.log(
        `✅ [${this._sugManagerId}] 所有 suggestions 已拒绝（单个 transaction 完成）`
      );
    } catch (error) {
      console.error(`❌ [${this._sugManagerId}] Reject All 失败:`, error);
    }
  }

  /**
   * ✅ 新增：清理所有 suggestions
   * - 清理所有 blocks 的 suggestionState
   * - 清空 ySuggestionInfo
   * - 用于数据不一致时的清理
   */
  async clearAllSuggestions(): Promise<void> {
    const store = this._getStore();
    if (!store) {
      console.warn(`⚠️ [${this._sugManagerId}] Store 未就绪`);
      return;
    }

    const suggestions = this._getSuggestionsFromY();

    if (suggestions.length === 0) {
      console.log(`ℹ️ [${this._sugManagerId}] ySuggestionInfo 已为空`);
      return;
    }

    console.log(
      `🧹 [${this._sugManagerId}] 清理所有 suggestions: ${suggestions.length}`
    );

    // ✅ 在 transaction 中清理
    store.transact(() => {
      // 1. 清理所有 blocks 的 suggestionState
      suggestions.forEach(s => {
        const allBlockIds = [...s.targetBlockIds, ...(s.newBlockIds || [])];

        allBlockIds.forEach(blockId => {
          const block = store.getBlock(blockId);
          if (block) {
            block.yBlock.delete('suggestionState');
          }
        });
      });

      // 2. 清空 ySuggestionInfo
      if (this._ySuggestionInfo) {
        this._ySuggestionInfo.set('list', { list: [] });
      }
    });

    console.log(`✅ [${this._sugManagerId}] 所有 suggestions 已清理`);

    // ✅ observe 会自动触发 UI 清理
  }

  /**
   * 获取单个 suggestion
   */
  getSuggestion(suggestionId: string): any {
    const suggestions = this._getSuggestionsFromY();
    const suggestion = suggestions.find(s => s.id === suggestionId);

    if (!suggestion) return null;

    return {
      suggestion,
      state: suggestion.status || 'pending',
    };
  }

  /**
   * 获取统计信息
   */
  getSuggestionStats(): any {
    const suggestions = this._getSuggestionsFromY();
    return {
      total: suggestions.length,
      pending: suggestions.filter(s => s.status === 'pending').length,
      accepted: suggestions.filter(s => s.status === 'accepted').length,
      rejected: suggestions.filter(s => s.status === 'rejected').length,
    };
  }

  // ============================================
  // 私有方法
  // ============================================

  private _initializeWithObserve(): void {
    try {
      const store = this._docService.doc?.blockSuiteDoc;
      if (!store) {
        console.warn(`⚠️ [${this._sugManagerId}] Store 未就绪，延迟初始化`);
        setTimeout(() => {
          if (!this._disposed) {
            this._initializeWithObserve();
          }
        }, 500);
        return;
      }

      this._ySuggestionInfo = store.ySuggestionInfo;
      this._yObserver = this._handleYSuggestionChange.bind(this);
      this._ySuggestionInfo.observe(this._yObserver);

      console.log(`✅ [${this._sugManagerId}] Observe 已设置`);

      // 初始同步
      this._syncUIFromY();
    } catch (error) {
      console.error(`❌ [${this._sugManagerId}] 初始化失败:`, error);
    }
  }

  private _handleYSuggestionChange(event: any): void {
    console.log(`🔔 [${this._sugManagerId}] ySuggestionInfo 变化:`, event);

    // ✅ 使用微任务延迟，让 BlockSuite 有机会更新 DOM
    // UIManager 的 MutationObserver 会处理等待和中断逻辑
    Promise.resolve()
      .then(() => {
        if (!this._disposed) {
          this._syncUIFromY();
        }
      })
      .catch(error => {
        console.error(`❌ [${this._sugManagerId}] UI 同步失败:`, error);
      });
  }

  private _syncUIFromY(): void {
    try {
      const suggestions = this._getSuggestionsFromY();
      console.log(
        `🔄 [${this._sugManagerId}] 同步 UI: ${suggestions.length} suggestions`
      );
      this._uiManager.syncSuggestions(suggestions);
    } catch (error) {
      console.error(`❌ [${this._sugManagerId}] UI 同步失败:`, error);
    }
  }

  private _getSuggestionsFromY(): Suggestion[] {
    try {
      if (!this._ySuggestionInfo) return [];
      const data = this._ySuggestionInfo.get('list');
      return data?.list || [];
    } catch (error) {
      console.error(`❌ [${this._sugManagerId}] 读取 suggestions 失败:`, error);
      return [];
    }
  }

  private _getStore(): any {
    return this._docService.doc?.blockSuiteDoc || null;
  }
}
